import {
  MapColor,
  MappedVVRenderer,
  SceneDirection,
  SceneOverEvent,
} from "./MappedVVRenderer.class";
import { SituationMap } from "./situationMap.class";

type SceneDefinition = {
  medias: (MediaDefinition | VisiteDefinition)[];
  mapColors: MapColor[];
};

interface MediaDefinition {
  key: string;
  type: string;
  url: string;
  coords: CameraDefinition;
}

interface CameraDefinition {
  position: [number, number, number];
  rotation: [number, number, number];
  target?: [number, number, number];
  fov?: number;
}

interface VisiteDefinition extends MediaDefinition {
  mapUrl: string;
}

class VV {
  renderer: MappedVVRenderer;
  situationMap: SituationMap;
  element: HTMLElement;
  sceneDefinition: SceneDefinition;
  currentScene: VisiteDefinition;
  transitionImg: HTMLImageElement;
  mapContainer: HTMLElement;
  public onLoadChange: (val: boolean) => void;

  constructor() {
    this.element = document.createElement("div");
    this.element.style.position = "relative";
    this.element.style.overflow = "hidden";
    this.element.style.height = "100%";
    this.renderer = new MappedVVRenderer();
    const rendererElt = this.renderer.getDomElement();
    this.renderer.onDirectionClic = this.onDirectionClic.bind(this);
    const mapContainer = document.createElement("div");
    mapContainer.style.position = "absolute";
    mapContainer.style.width = "800px";
    mapContainer.style.height = "450px";
    mapContainer.style.top = "10px";
    mapContainer.style.right = "10px";
    mapContainer.style.transformOrigin = "top right";
    mapContainer.style.transform = "scale(0.5)";
    mapContainer.style.transition = "transform 0.3s";
    mapContainer.classList.add("mapContainer");
    const styleTag = document.createElement("style");
    styleTag.innerHTML = `.mapContainer:hover {transform: scale(1) !important;}`;
    document.head.insertAdjacentElement("beforeend", styleTag);
    this.situationMap = new SituationMap();
    this.situationMap.onclickhotspot = this.onClickHotspot.bind(this);
    const situationElt = this.situationMap.getDomElement();

    mapContainer.append(situationElt);
    this.element.append(rendererElt, mapContainer);
    this.mapContainer = mapContainer;
    requestAnimationFrame(this.animateHotspots.bind(this));
  }

  getDomElement() {
    return this.element;
  }

  animateHotspots() {
    if (this.currentScene) {
      const camera = this.renderer.projector.camera;
      const [lng, lat] = this.renderer.projector.getLongLat();

      const cameraRotation = this.currentScene.coords.rotation[2];
      const rotation = ((lng / Math.PI) * -180 - cameraRotation) % 360;

      this.situationMap.updateHotspot(this.currentScene.key, {
        angle: camera.fov,
        rotation: rotation,
      });
    }
    requestAnimationFrame(this.animateHotspots.bind(this));
  }

  onClickHotspot({ key, data }: { key: string; data: any }) {
    const scene = this.sceneDefinition.medias.find(
      (s) => s.key === key
    ) as VisiteDefinition;
    this.setScene(scene);
  }

  setVisite(sceneDefinition: SceneDefinition, firstSceneName?: string) {
    this.sceneDefinition = sceneDefinition;
    this.situationMap.setScene(sceneDefinition);
    const scenes = sceneDefinition.medias.filter((m) => m.type === "visite");
    const scene = (
      firstSceneName ? scenes.find((s) => s.key === firstSceneName) : scenes[0]
    ) as VisiteDefinition;
    this.setScene(scene);
  }

  getDistances() {
    const scene = this.currentScene;
    const scenes = this.sceneDefinition.medias.filter(
      (m) => m.type === "visite"
    );
    const [x1, y1, z1] = scene.coords.position;
    return scenes
      .map((s) => {
        const result: { scene: MediaDefinition; distance: number } = {
          scene: null,
          distance: null,
        };
        result.scene = s;
        const [x2, y2, z2] = s.coords.position;
        result.distance = Math.sqrt(
          (x2 - x1) * (x2 - x1) + (y2 - y1) * (y2 - y1) + (z2 - z1) * (z2 - z1)
        );
        return result;
      })
      .sort((a, b) => a.distance - b.distance);
  }

  onDirectionClic(evt: SceneOverEvent) {
    this.setScene(evt.data);
  }

  initTransition() {
    const sourceCanvas = this.renderer.projector.getDomElement();
    const img = new Image();
    img.style.position = "absolute";
    img.style.width = "100%";
    img.style.height = "100%";
    img.style.top = "0";
    img.height = sourceCanvas.height;
    img.width = sourceCanvas.width;
    this.element.insertBefore(img, this.mapContainer);
    img.src = sourceCanvas.toDataURL();
    this.transitionImg = img;
  }

  runTransition() {
    requestAnimationFrame(() => {
      this.transitionImg.style.transition =
        "opacity 2s ease-in-out, transform 2s ease-in-out";
      this.transitionImg.style.opacity = "0";
      this.transitionImg.style.transform = "scale(1.4)";
      this.transitionImg.style.transformOrigin = "center";
      setTimeout(() => {
        this.transitionImg.parentNode.removeChild(this.transitionImg);
      }, 2000);
    });
  }

  async setScene(scene: VisiteDefinition) {
    if (this.onLoadChange) {
      this.onLoadChange(true);
    }
    this.initTransition();
    this.currentScene = scene;
    await this.renderer.setScene(scene, this.sceneDefinition.mapColors);
    this.situationMap.activateHotspot(scene.key);
    const distances = this.getDistances().filter(
      (d) => d.distance < 2500 && d.distance > 0
    );
    distances.forEach(({ scene }) => {
      const direction: SceneDirection = {
        data: scene,
        vector: [
          scene.coords.position[0] - this.currentScene.coords.position[0],
          scene.coords.position[1] - this.currentScene.coords.position[1],
        ],
      };
      this.renderer.addDirection(
        direction,
        (this.currentScene.coords.rotation[2] * Math.PI) / 180
      );
    });
    if (this.onLoadChange) {
      this.onLoadChange(false);
    }
    this.runTransition();
  }
}

export {
  VV,
  MediaDefinition,
  CameraDefinition,
  SceneDefinition,
  VisiteDefinition,
};
